/**
 * Override of SplideJS initialization to load Splide JS Extensions and add accessibility attributes.
 *
 * @see https://forum.bricksbuilder.io/t/wip-add-support-for-splide-autoscroll/5379/8
 * @see https://gist.github.com/joseadrian/1edc5dfb8078a0910c1910ffbb644b4c
 */

window.bricksSplideFn = new BricksFunction({
	parentNode: document,
	selector: '.brxe-slider-nested.splide',
	windowVariableCheck: ['Splide'],
	forceReinit: (element, index) => {
		// Allow force reinit inside builder
		return !bricksIsFrontend;
	},
	eachElement: (splideElement) => {
		// Add .splide__slide to individual slide (perfect for in/builder)
		let slides = bricksQuerySelectorAll(splideElement, [
			'.splide__list > .brxe-container',
			'.splide__list > .brxe-block',
			'.splide__list > .brxe-div'
		]);

		slides.forEach((slide) => {
			slide.classList.add('splide__slide');
			slide.dataset.id = slide.id;
		});

		let scriptId = splideElement.dataset.scriptId;

		// Destroy existing splideJS instance
		if (window.bricksData.splideInstances.hasOwnProperty(scriptId)) {
			window.bricksData.splideInstances[scriptId].destroy();
		}

		// Init & mount splideJS
		let splideInstance = new Splide(splideElement);

		splideInstance.on('mounted', () => {
			splideElement.querySelectorAll('.splide__slide').forEach((slide) => {
				slide.setAttribute('role', 'presentation');
				slide.removeAttribute('aria-label');
			});
		});

		// Kelcible - START - Dynamically load and mount Splide JS Extensions based on data attributes on the splideElement
		const extensions = window.splide?.Extensions || {};
		const extensionToMount = {};

		for (const [extensionName, extensionClass] of Object.entries(extensions)) {
			if (splideElement.hasAttribute(extensionName.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase())) {
				extensionToMount[extensionName] = extensionClass;
			}
		}
		// Kelcible - END

		// https://splidejs.com/guides/apis/#go
		splideInstance.mount(extensionToMount);

		let splideOptions = {};

		try {
			splideOptions = JSON.parse(splideElement.dataset.splide);

			// Add i18n configuration (@since 1.12.2)
			// See: https://splidejs.com/guides/i18n/#default-texts
			splideOptions.i18n = {
				prev: window.bricksData.i18n.prevSlide,
				next: window.bricksData.i18n.nextSlide,
				first: window.bricksData.i18n.firstSlide,
				last: window.bricksData.i18n.lastSlide,
				slideX: window.bricksData.i18n.slideX,
				pageX: window.bricksData.i18n.slideX, // Reuse slideX for pageX
				play: window.bricksData.i18n.play,
				pause: window.bricksData.i18n.pause,
				carousel: window.bricksData.i18n.splide.carousel,
				select: window.bricksData.i18n.splide.select,
				slide: window.bricksData.i18n.splide.slide,
				slideLabel: window.bricksData.i18n.splide.slideLabel
			};
		} catch (e) {
			console.warn('bricksSplide: Error parsing JSON of data-script-args', scriptId);
		}

		// Set auto height
		if (splideOptions?.autoHeight) {
			let updateHeight = () => {
				try {
					// Ensure splideInstance and Components are defined
					let slidesComponent = splideInstance?.Components?.Slides;
					if (!slidesComponent) {
						console.error('Slides component is undefined:', scriptId);
						return;
					}

					let slideObject = slidesComponent.getAt(splideInstance.index);

					// Ensure slideObject and slide are defined
					let slide = slideObject?.slide;
					if (!slide) {
						console.error('Slide is undefined:', scriptId);
						return;
					}

					// Ensure slide.parentElement is defined before setting style
					let parentElement = slide.parentElement;
					if (!parentElement) {
						console.error('Parent element is undefined:', scriptId);
						return;
					}

					// Get height of largest visible slide (in case "Items to show" > 1)
					let autoHeight = 0;
					slides.forEach((slide) => {
						if (slide.classList.contains('is-visible') && slide.offsetHeight > autoHeight) {
							autoHeight = slide.offsetHeight;
						}
					});

					if (!autoHeight) {
						autoHeight = slide.offsetHeight;
					}

					parentElement.style.height = `${autoHeight}px`;
				} catch (error) {
					// Log any other unexpected errors
					console.error('An error occurred while updating the height:', error, scriptId);
				}
			};

			// Call updateHeight if splideInstance is defined
			if (splideInstance) {
				updateHeight();

				// Attach event listeners if splideInstance.on is a function
				if (typeof splideInstance.on === 'function') {
					splideInstance.on('move resize', updateHeight);
					splideInstance.on('moved', updateHeight); // Needed when "Items to show" > 1 to get visible slides
				} else {
					console.error('splideInstance.on is not a function');
				}
			} else {
				console.error('splideInstance is undefined');
			}
		}

		// Store splideJS instance in bricksData to destroy and re-init
		window.bricksData.splideInstances[scriptId] = splideInstance;

		// NOTE: To ensure Bricks element ID is used (important also for builder), and not the randomly by splide generated ID (see: slide.js:mount())
		// Improvement: Tweak CSS selector for 'bricksSplide' elements to use #parent.id > .{slide-class}
		slides.forEach((slide, index) => {
			if (slide.dataset.id) {
				slide.id = slide.dataset.id;

				// Set 'aria-controls' value to slide.id
				let pagination = splideElement.querySelector('.splide__pagination');

				if (pagination) {
					let paginationButton = pagination.querySelector(`li:nth-child(${index + 1}) .splide__pagination__page`);

					if (paginationButton) {
						paginationButton.setAttribute('aria-controls', slide.id);
					}
				}
			}

			// Get & set background-image added via lazy load through 'data-style' attribute inside query loop
			if (!slide.classList.contains('bricks-lazy-hidden')) {
				let style = slide.getAttribute('style') || '';

				if (slide.dataset.style) {
					style += slide.dataset.style;
					slide.setAttribute('style', style);
				}
			}
		});

		// Listen to bricks/tabs/changed event to update splideJS instance (@since 1.11.1)
		if (splideElement.closest('.tab-pane')) {
			document.addEventListener('bricks/tabs/changed', (event) => {
				splideInstance.refresh();
			});
		}
	}
});
